import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Entrada, Formulario, Rotulo, type DefCampo, type Opcao } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { limpar, nomeDoPapel, type Funcao, type ItemModelo } from './tipos';

/** M7.6: modelo do checklist de onboarding que quem ocupa a função recebe na admissão. */
function ModeloOnboarding({ funcao, podeEditar }: { funcao: Funcao; podeEditar: boolean }) {
  const { t } = useTranslation('pessoas');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [titulo, setTitulo] = useState('');
  const [prazo, setPrazo] = useState('');
  const chave = ['pessoas', 'onboarding-modelo', funcao.id];
  const q = useQuery({ queryKey: chave, queryFn: () => get<{ itens: ItemModelo[] }>(`/pessoas/funcoes/${funcao.id}/onboarding`) });
  const recarregar = async () => {
    await qc.invalidateQueries({ queryKey: chave });
    await qc.invalidateQueries({ queryKey: ['/pessoas/funcoes'] });
  };
  const adicionar = useMutation({
    mutationFn: () => post(`/pessoas/funcoes/${funcao.id}/onboarding`, { titulo, prazo_dias: prazo === '' ? null : Number(prazo) }),
    onSuccess: async () => {
      setTitulo('');
      setPrazo('');
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const remover = useMutation({
    mutationFn: (id: string) => del(`/pessoas/onboarding-itens/${id}`),
    onSuccess: recarregar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  return (
    <section className="mt-6 border-t border-borda pt-4" aria-label={t('funcoes.onboarding')}>
      <h3 className="kicker mb-1">{t('funcoes.onboarding')}</h3>
      <p className="mb-3 text-xs text-tinta-3">{t('funcoes.onboardingAjuda')}</p>
      <Estado consulta={q} vazio={(d) => d.itens.length === 0}>
        {(d) => (
          <ol className="divide-y divide-borda">
            {d.itens.map((i, n) => (
              <li key={i.id} className="flex min-w-0 items-center justify-between gap-3 py-2 text-sm">
                <span className="flex min-w-0 items-baseline gap-2">
                  <span className="numero text-xs text-tinta-3">{n + 1}</span>
                  <span className="truncate" title={i.titulo}>{i.titulo}</span>
                </span>
                <span className="flex items-center gap-3">
                  {i.prazo_dias !== null && <span className="numero text-xs text-tinta-3">{t('funcoes.prazoDias', { n: i.prazo_dias })}</span>}
                  {podeEditar && (
                    <button type="button" aria-label={tn('comum.excluir')} onClick={() => remover.mutate(i.id)} className="rounded p-1 text-tinta-3 hover:text-perigo">
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ol>
        )}
      </Estado>
      {podeEditar && (
        <form
          className="mt-3 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (titulo.trim()) adicionar.mutate();
          }}
        >
          <div className="min-w-0 flex-1 basis-48">
            <Rotulo id="item-titulo" rotulo={t('funcoes.novoItem')}>
              <Entrada id="item-titulo" value={titulo} maxLength={200} onChange={(e) => setTitulo(e.target.value)} />
            </Rotulo>
          </div>
          <div className="w-28">
            <Rotulo id="item-prazo" rotulo={t('funcoes.prazo')}>
              <Entrada id="item-prazo" type="number" min={0} max={365} className="numero" value={prazo} onChange={(e) => setPrazo(e.target.value)} />
            </Rotulo>
          </div>
          <Botao type="submit" variante="secundario" icone={<Plus className="size-4" />} carregando={adicionar.isPending} disabled={!titulo.trim()}>
            {t('acoes.adicionar')}
          </Botao>
        </form>
      )}
    </section>
  );
}

/** Ficha de 29/10 (item 2): papel do select "Papel sugerido", com `concedivel` pela régua do convite. */
interface PapelFuncao {
  id: string;
  nome: string;
  codigo: string;
  de_fabrica: boolean;
  concedivel: boolean;
}

/**
 * M7.2: funções (cargos) com departamento e papel de permissões herdadas. Ficha de 29/10 (item 2, opção B): para quem edita,
 * o "Papel sugerido" é uma lista com todos os papéis; os que ele não pode conceder ficam desabilitados, com o motivo no
 * rótulo da opção (o leitor de tela lê) e na ajuda do campo. O papel já gravado acima dele segue selecionado e salvar os
 * outros campos o mantém (o servidor só confere a concessão quando o papel muda).
 */
export function Funcoes() {
  const { t } = useTranslation('pessoas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<Funcao | 'nova' | null>(null);
  const [remover, setRemover] = useState<Funcao | null>(null);
  const atual = aberto && aberto !== 'nova' ? aberto : null;
  const recarregar = () => qc.invalidateQueries({ queryKey: ['/pessoas/funcoes'] });
  const podeEditar = s.pode('pessoas.configurar');
  const papeis = useQuery({
    queryKey: ['/pessoas/funcionarios/papeis-funcao'],
    queryFn: () => get<{ itens: PapelFuncao[] }>('/pessoas/funcionarios/papeis-funcao'),
    enabled: podeEditar && !!aberto,
  });
  const opcoesPapel: Opcao[] = (papeis.data?.itens ?? []).map((p) => {
    const nome = nomeDoPapel(t, { nome: p.nome, codigo: p.codigo, de_fabrica: p.de_fabrica });
    return p.concedivel ? { valor: p.id, rotulo: nome } : { valor: p.id, rotulo: t('funcoes.papelAcima', { nome }), desabilitado: true };
  });
  // O papel gravado que a leitura não trouxe (ainda carregando, ou excluído) continua selecionado, desabilitado.
  if (atual?.papel_id && !opcoesPapel.some((o) => o.valor === atual.papel_id))
    opcoesPapel.unshift({ valor: atual.papel_id, rotulo: t('funcoes.papelAcima', { nome: atual.papel_nome ? nomeDoPapel(t, { nome: atual.papel_nome }) : '-' }), desabilitado: true });
  const algumAcima = opcoesPapel.some((o) => o.desabilitado);

  const excluir = useMutation({
    mutationFn: (id: string) => del(`/pessoas/funcoes/${id}`),
    onSuccess: async () => {
      setRemover(null);
      setAberto(null);
      await recarregar();
    },
    onError: (e) => {
      setRemover(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  const colunas: Coluna<Funcao>[] = [
    { chave: 'nome', rotulo: t('campos.nome'), ordenavel: true, principal: true, render: (f) => <span className="font-medium">{f.nome}</span> },
    { chave: 'departamento_nome', rotulo: t('campos.departamento') },
    { chave: 'papel_nome', rotulo: t('campos.papel'), render: (f) => (f.papel_nome ? nomeDoPapel(t, { nome: f.papel_nome }) : '-') },
    { chave: 'funcionarios', rotulo: t('funcoes.funcionarios'), alinhar: 'direita', mono: true },
    { chave: 'itens_onboarding', rotulo: t('funcoes.itensOnboarding'), alinhar: 'direita', mono: true },
    { chave: 'descricao', rotulo: t('campos.descricao'), oculta: true },
  ];

  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true },
    { nome: 'departamento_id', rotulo: t('campos.departamento'), tipo: 'busca', largura: 'meia', busca: { caminho: '/pessoas/departamentos', rotulo: (i) => String(i.nome) } },
    podeEditar
      ? { nome: 'papel_id', rotulo: t('campos.papel'), tipo: 'selecao', largura: 'meia', ajuda: algumAcima ? `${t('campos.papelAjuda')} ${t('funcoes.papelAcimaAjuda')}` : t('campos.papelAjuda'), opcoes: opcoesPapel }
      : { nome: 'papel_id', rotulo: t('campos.papel'), tipo: 'busca', largura: 'meia', ajuda: t('campos.papelAjuda'), busca: { caminho: '/pessoas/papeis', rotulo: (i) => nomeDoPapel(t, { nome: String(i.nome), codigo: String(i.codigo), de_fabrica: i.de_fabrica as boolean }) } },
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'area' },
  ];

  return (
    <>
      {s.pode('pessoas.configurar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('nova')}>
            {t('acoes.novaFuncao')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Tabela<Funcao> tela="pessoas.funcoes" caminho="/pessoas/funcoes" colunas={colunas} aoClicar={(f) => setAberto(f)} vazio={<Vazio texto={t('vazio.funcoes')} />} />
      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        largura="lg"
        titulo={atual ? atual.nome : t('acoes.novaFuncao')}
        rodape={
          <>
            {atual && s.pode('pessoas.configurar') && (
              <Botao variante="perigo" className="mr-auto" onClick={() => setRemover(atual)}>
                {tn('comum.excluir')}
              </Botao>
            )}
            {s.pode('pessoas.configurar') && (
              <>
                <Botao variante="fantasma" onClick={() => setAberto(null)}>
                  {tn('comum.cancelar')}
                </Botao>
                <Botao variante="primario" type="submit" form="form-funcao">
                  {tn('comum.salvar')}
                </Botao>
              </>
            )}
          </>
        }
      >
        <Formulario
          key={atual?.id ?? 'nova'}
          id="form-funcao"
          campos={campos}
          inicial={(atual ?? {}) as unknown as Record<string, unknown>}
          rotulosAtuais={atual ? { departamento_id: atual.departamento_nome ?? '', papel_id: atual.papel_nome ? nomeDoPapel(t, { nome: atual.papel_nome }) : '' } : undefined}
          aoEnviar={async (v) => {
            const corpo = limpar({ nome: v.nome, departamento_id: v.departamento_id, papel_id: v.papel_id, descricao: v.descricao });
            if (atual) await patch(`/pessoas/funcoes/${atual.id}`, corpo);
            else await post('/pessoas/funcoes', corpo);
            await recarregar();
            toast.sucesso(tn('comum.salvo'));
            setAberto(null);
          }}
        />
        {atual && <ModeloOnboarding funcao={atual} podeEditar={s.pode('pessoas.configurar')} />}
      </Drawer>
      <Confirmar aberto={!!remover} aoFechar={() => setRemover(null)} aoConfirmar={() => remover && excluir.mutate(remover.id)} titulo={t('confirmar.excluirFuncao')} texto={t('confirmar.excluirFuncaoTexto')} perigoso carregando={excluir.isPending} />
    </>
  );
}
